import { WIDGET_SCOPE_ATTRIBUTE } from './widget-markers';

/** Elements worth naming on their own: a click on the icon inside a button is a click on the button. */
const INTERACTIVE_TAGS: ReadonlySet<string> = new Set([
  'a',
  'button',
  'input',
  'select',
  'textarea',
  'label',
  'summary',
  'option'
]);

/** Attributes that name an element without telling what it shows, most telling first. */
const NAMING_ATTRIBUTES = ['data-testid', 'name', 'id', 'role'] as const;

/**
 * Attributes a developer wrote to label a control for people. Read on controls only: on a
 * container or an image the same attributes as often name what is shown (`alt` of an
 * avatar, `aria-label` of a client's section), so those get none.
 */
const LABEL_ATTRIBUTES = ['aria-label', 'title', 'placeholder'] as const;

/** Controls whose visible text is their label, not the widget's data. */
const LABELLED_BY_TEXT =
  'button, summary, label, [role=button], [role=menuitem], [role=menuitemcheckbox], [role=menuitemradio], [role=tab], [role=link]';

/** Controls labelled by attributes or an associated `<label>`, never by their text (a textarea's text is its value). */
const FORM_CONTROLS = 'input, textarea, select';

/** Where a link's text is a label; anywhere else a link is as likely a client's name. */
const LINK_LABEL_CONTEXTS =
  'nav, header, aside, [role=navigation], [role=menu], [role=menubar], [role=tablist]';

/** Where text is data whatever the element: the cells of a table, the choices of a list. */
const DATA_CONTEXTS =
  'td, th, [role=gridcell], [role=cell], [role=rowheader], [role=columnheader], [role=listbox], [role=option], option, select, datalist';

/** Ancestors that place an element: a dialog, a form, a region a developer marked. */
const CONTEXT_SELECTOR =
  '[data-testid], [aria-label], [role=dialog], [role=alertdialog], [role=navigation], [role=menu], [role=tablist], [role=grid], [role=table], [role=region], dialog, nav, form, table, header, footer, aside, main';

/** The class an icon library puts on its svg: `lucide-trash-2`, `fa-user`. */
const ICON_CLASS = /^(?:lucide|icon|fa|bi|mdi)-(.+)$/;

/** A ligature icon's name: `delete`, `warning_amber`. */
const ICON_NAME = /^[a-z][a-z0-9_-]{0,23}$/;

const MAX_NAME_LENGTH = 40;

/** Longer than this is content, not a label. */
const MAX_LABEL_LENGTH = 40;

function isInteractive(element: Element): boolean {
  return (
    INTERACTIVE_TAGS.has(element.localName) ||
    element.hasAttribute('role') ||
    element.hasAttribute('data-testid')
  );
}

/** The element an event is about: the first interactive one on its path, else the target. */
export function interactiveTarget(path: readonly EventTarget[]): Element | undefined {
  let target: Element | undefined;
  for (const node of path) {
    if (!(node instanceof Element)) {
      continue;
    }
    if (target === undefined) {
      target = node;
    }
    if (isInteractive(node)) {
      return node;
    }
  }
  return target;
}

function clip(value: string): string {
  return value.length <= MAX_NAME_LENGTH ? value : `${value.slice(0, MAX_NAME_LENGTH)}…`;
}

/** `client-row-48213` becomes `client-row-#`: the kind of thing stays, the record's id goes. */
export function maskDigits(value: string): string {
  return value.replace(/\d+/g, '#');
}

/**
 * Whether a text is a label rather than data: short, and free of digits and `@`, which
 * names of records, amounts, dates and addresses are not.
 */
export function isLabelLike(text: string): boolean {
  return text.length > 0 && text.length <= MAX_LABEL_LENGTH && !/[\d@]/.test(text);
}

function isIconElement(element: Element): boolean {
  return (
    element.localName === 'svg' ||
    element.localName === 'i' ||
    element.getAttribute('aria-hidden') === 'true' ||
    /icon/i.test(element.getAttribute('class') ?? '')
  );
}

/** The text a person sees on an element: icons and hidden parts left out, whitespace folded. */
export function visibleText(element: Element): string {
  const parts: string[] = [];
  const collect = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      parts.push(node.textContent ?? '');
      return;
    }
    if (node instanceof Element && !isIconElement(node)) {
      node.childNodes.forEach(collect);
    }
  };
  element.childNodes.forEach(collect);
  return parts.join(' ').replace(/\s+/g, ' ').trim();
}

/** A link reads as a label inside a navigation or a menu; anywhere else it may be a name. */
function isNavigationLink(element: Element): boolean {
  return element.localName === 'a' && element.closest(LINK_LABEL_CONTEXTS) !== null;
}

function attributeLabel(element: Element): string | undefined {
  if (
    !element.matches(LABELLED_BY_TEXT) &&
    !element.matches(FORM_CONTROLS) &&
    !isNavigationLink(element)
  ) {
    return undefined;
  }
  for (const attribute of LABEL_ATTRIBUTES) {
    const value = element.getAttribute(attribute)?.trim();
    if (value && isLabelLike(value)) {
      return value;
    }
  }
  return undefined;
}

function formLabel(element: Element): string | undefined {
  const label = (element as HTMLInputElement).labels?.[0];
  if (!label) {
    return undefined;
  }
  const text = visibleText(label);
  return isLabelLike(text) ? text : undefined;
}

function textLabel(element: Element): string | undefined {
  if (!element.matches(LABELLED_BY_TEXT) && !isNavigationLink(element)) {
    return undefined;
  }
  const text = visibleText(element);
  return isLabelLike(text) ? text : undefined;
}

/**
 * What a control is called, as a developer labelled it or as it reads. Nothing for
 * anything but a control, and nothing inside a table cell or an option list, where even a
 * button's label tends to name the record it acts on.
 */
export function labelOf(element: Element): string | undefined {
  if (element.closest(DATA_CONTEXTS) !== null) {
    return undefined;
  }
  return attributeLabel(element) ?? formLabel(element) ?? textLabel(element);
}

/**
 * Where a link goes, with every path segment that holds a digit masked:
 * `/clients/#/accounts`. Nothing for a `mailto:`, a `tel:` or a script.
 */
export function maskedHref(href: string | null): string | undefined {
  if (href === null || !/^(?:\/|https?:)/.test(href)) {
    return undefined;
  }
  const path = href.split(/[?#]/, 1)[0] ?? '';
  return path
    .split('/')
    .map((segment) => (/\d/.test(segment) ? '#' : segment))
    .join('/');
}

/** The icon an icon-only control shows: an icon library's class, or a ligature's text. */
export function iconOf(element: Element): string | undefined {
  const svg = element.querySelector('svg');
  for (const className of (svg?.getAttribute('class') ?? '').split(/\s+/)) {
    const match = ICON_CLASS.exec(className);
    if (match) {
      return match[1];
    }
  }
  const ligature = element.querySelector('i, [class*="icon" i]');
  const name = ligature?.textContent?.trim() ?? '';
  return ICON_NAME.test(name) ? name : undefined;
}

/** `:nth-of-type(3)` among siblings of the same tag, when there are others to tell apart. */
function position(element: Element): string {
  const siblings = Array.from(element.parentElement?.children ?? []).filter(
    (sibling) => sibling.localName === element.localName
  );
  return siblings.length > 1 ? `:nth-of-type(${siblings.indexOf(element) + 1})` : '';
}

/** The tag, the type and the most telling naming attribute: what every description starts with. */
function nameOf(element: Element): { description: string; named: boolean } {
  let description = element.localName;
  const type = element.getAttribute('type');
  if (type && (element.localName === 'input' || element.localName === 'button')) {
    description += `[type=${clip(type)}]`;
  }
  for (const attribute of NAMING_ATTRIBUTES) {
    const value = element.getAttribute(attribute);
    if (value) {
      return {
        description: `${description}[${attribute}=${clip(maskDigits(value))}]`,
        named: true
      };
    }
  }
  return { description, named: false };
}

/**
 * An element on its own, like a terse selector with the label a person sees:
 * `button[data-testid=save] "Save"`, `a[href=/clients/#] "Compliance"`,
 * `button[icon=trash-2]`, `input[type=text][name=email] "Email"`. Without anything to name
 * it, its position among its siblings: `li:nth-of-type(3)`. Values and content are
 * never part of it.
 */
export function describeElement(element: Element): string {
  let { description, named } = nameOf(element);
  const href = element.localName === 'a' ? maskedHref(element.getAttribute('href')) : undefined;
  if (href) {
    description += `[href=${clip(href)}]`;
    named = true;
  }
  const icon = iconOf(element);
  if (icon) {
    description += `[icon=${icon}]`;
    named = true;
  }
  const label = labelOf(element);
  if (label) {
    description += ` "${clip(label)}"`;
    named = true;
  }
  return named ? description : description + position(element);
}

/**
 * The nearest ancestor that places the element, up to its widget's card or the body: a
 * dialog, a form, a navigation, a region a developer marked. The card itself is the
 * entry's scope already and names nothing.
 */
export function contextOf(element: Element): Element | undefined {
  let node = element.parentElement;
  while (node !== null && node !== document.body && !node.hasAttribute(WIDGET_SCOPE_ATTRIBUTE)) {
    if (node.matches(CONTEXT_SELECTOR)) {
      return node;
    }
    node = node.parentElement;
  }
  return undefined;
}

/**
 * The element with its context: `div[role=dialog] › button "Save"`. The context is named
 * by its attributes alone: a dialog's or a section's label is as often the record it shows.
 */
export function describeTarget(element: Element): string {
  const own = describeElement(element);
  const context = contextOf(element);
  return context === undefined ? own : `${nameOf(context).description} › ${own}`;
}
